<script setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

  import DateBox from "./chat/DateBox.vue";
  import EnterBox from "./chat/EnterBox.vue";
  import ChatMessage from "./chat/ChatMessage.vue";
  import MonitoringCard from "@/components/my-test/teacher/tour-watch/MonitoringCard.vue";
  import { getUserInfo } from "@/lib/api";

  /**
   * Props
   */
  const props = defineProps({
    isInWorkingTime: {
      type: Boolean
    },
    dmApiParam: {
      type: Object,
      default: () => ({}),
    },
    dmMessageList: {
      type: Array,
      default: () => [],
    },
    reloadEvent: { type: Function, required: true },
  });

  /**
   * Functions
   */

  function isSameDate(date1, date2) {
    return date1 === date2;
  }

  function formatDmTitleDate(date) {
    // 1. 문자열에서 년, 월, 일 추출
    const year = parseInt(date.slice(0, 4), 10);
    const month = parseInt(date.slice(4, 6), 10) - 1; // JS Date의 월은 0~11
    const day = parseInt(date.slice(6, 8), 10);

    // 2. Date 객체 생성
    return new Date(year, month, day);
  }

  async function scrollDown() {
    await nextTick();
    const chatContainer = document.getElementById('chatContainer');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }

  // 도움말 페이지 열기
  function openHelpPage() {
    window.open('https://sites.google.com/nsdevil.com/senedu', '_blank', 'noopener,noreferrer');
  }

  /**
   * 이미지 드래그 앤 드랍 첨부
   */
  const enterBoxRef = ref(null);
  // 자식 요소 진입/이탈 시 dragenter/dragleave가 반복 발생하므로 카운터로 관리
  const dragCounter = ref(0);
  const isDragging = computed(() => dragCounter.value > 0);

  function hasDragFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  function handleDragEnter(event) {
    if (!hasDragFiles(event)) return;
    event.preventDefault();
    dragCounter.value++;
  }

  function handleDragOver(event) {
    if (!hasDragFiles(event)) return;
    event.preventDefault();
  }

  function handleDragLeave(event) {
    if (!hasDragFiles(event)) return;
    dragCounter.value = Math.max(0, dragCounter.value - 1);
  }

  function handleDrop(event) {
    if (!hasDragFiles(event)) return;
    event.preventDefault();
    dragCounter.value = 0;
    enterBoxRef.value?.addFiles(event.dataTransfer.files);
  }

  // 채팅 영역 밖에 놓았을 때 브라우저가 이미지 파일로 이동하는 것 방지
  function preventWindowDrop(event) {
    event.preventDefault();
  }

  onMounted(() => {
    window.addEventListener('dragover', preventWindowDrop);
    window.addEventListener('drop', preventWindowDrop);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('dragover', preventWindowDrop);
    window.removeEventListener('drop', preventWindowDrop);
  });

  defineExpose({
    scrollDown
  });
</script>

<template>
  <n-flex
    vertical
    class="chat-app relative h-full overflow-hidden"
    :size="0"
    @dragenter="handleDragEnter"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <div v-if="isDragging" class="drag-overlay">
      <p>이미지를 여기에 놓아주세요</p>
    </div>
    <div class="border-b px-4 py-3" style="flex: auto 0 0">
      <n-flex align="center" justify="space-between">
        <h3 class="text-lg">문의하기</h3>
        <ui-chip
          size="sm"
          @click="openHelpPage"
          class="cursor-pointer"
          style="background-color: #dc2626; border-color: #dc2626; color: white;"
        >
          도움말
        </ui-chip>
      </n-flex>
      <div class="mt-1">
        <p class="text-sm text-gray-600">
          평일 09:00 ~ 18:00 (점심시간 12:00~13:00)
        </p>
      </div>
    </div>
    <div class="overflow-hidden" style="flex: 1">
      <n-flex
        vertical
        align="stretch"
        justify="space-between"
        class="h-full bg-gray-100"
        :size="0"
      >
        <div id="chatContainer" style="flex: 1" class="overflow-auto">
          <template
            v-if="dmMessageList?.length > 0"
            v-for="(dmItem, index) in dmMessageList" :key="`dm-${index}`"
          >

            <!-- 이전 item과 날짜가 다르면 구분 div -->
            <div v-if="index === 0 || !isSameDate(dmItem.date, dmMessageList[index - 1].date)" class="date-separator">
              <DateBox :date="formatDmTitleDate(dmItem.date)" />
            </div>

            <n-flex vertical :size="8" class="px-4 pb-4">
              <ChatMessage
                :key="`chat-${index}`"
                :item="dmItem"
              />
            </n-flex>

          </template>
          <template v-else>
            <n-flex vertical :size="8" class="px-4 pb-4">
              <p class="text-center text-sm text-gray-500" style="margin-top: 40px;">
                내용이 없습니다.
              </p>
            </n-flex>
          </template>
        </div>
        <div style="flex: auto 0 0">
          <EnterBox
            ref="enterBoxRef"
            :dmApiParam="dmApiParam"
            :reloadEvent="reloadEvent"
          />
        </div>
      </n-flex>
    </div>
  </n-flex>
</template>

<style lang="scss" scoped>
  .chat-app {
    .drag-overlay {
      position: absolute;
      inset: 0;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.75);
      border: 2px dashed var(--smoe-primary-500);
      border-radius: 0.375rem;
      pointer-events: none;
      p {
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--smoe-primary-500);
      }
    }
  }
</style>
